// Functions for communicating with the backend API
// For endpoints that require API keys, a proxy is used

import * as con from "../Constants"

// Check if we're in development environment

// Direct API call (for public endpoints that don't need API key)
const directApiCall = async (path, method = 'GET') => {

  const URL = await con.getBackendUrl()

  try {
    const response = await fetch(`${URL}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    const isDevelopment = await con.isDevelopment()

    if (isDevelopment) {
      console.error('Direct API call failed:', error);
    }
    throw error;
  }
};

// Secure API call through Netlify function proxy (for endpoints requiring API key)
const secureApiCall = async (path, method = 'GET', data = null) => {

  try {
    let requestConfig = {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    if (method === 'GET') {
      // For GET requests, send path as query parameter (no body allowed)
      const url = new URL('/.netlify/functions/api-proxy', window.location.origin);
      url.searchParams.set('path', path);

      const response = await fetch(url.toString(), requestConfig);

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      return await response.json();
    } else {
      // For POST/PUT requests, send path and data in body
      const requestBody = { path, ...data };
      requestConfig.body = JSON.stringify(requestBody);

      const response = await fetch('/.netlify/functions/api-proxy', requestConfig);

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      return await response.json();
    }
  } catch (error) {
    const isDevelopment = await con.isDevelopment()

    if (isDevelopment) {
      console.error('Secure API call failed:', error);
    }
    throw error;
  }
};


// Health check (to disable the app if the server is not alive)
export const isServerAlive = async () => {
  try {
    const healthCheck = await checkServerHealth();
    return healthCheck.status === con.HEALTHY
  }
  catch (error) {
    return false
  }
}

/**
 * Check if the server is healthy before making requests
 * Public endpoint - no API key required, call directly to save function usage
 * @returns {Promise<{status: string, [key: string]: any}>}
 */
export const checkServerHealth = async () => {

  try {
    return await directApiCall('/api/v1/health');
  } catch (error) {
    const isDevelopment = await con.isDevelopment()

    if (isDevelopment) {
      console.error('Health check failed:', error);
    }
    throw error;
  }
};

/**
 * Upload a FASTA file to the server
 * Requires API key - use proxy
 * @param {File} file - The file to upload
 * @returns {Promise<{file_id: string, filename: string, upload_status: string, message: string}>}
 */
export const uploadFile = async (file) => {


  try {
    const formData = new FormData();
    formData.append('file', file);

    // Use proxy for file upload (requires API key)
    const response = await fetch('/.netlify/functions/api-proxy?path=/api/v1/upload', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    const isDevelopment = await con.isDevelopment()
    if (isDevelopment) {
      console.error('File upload failed:', error);
    }
    throw error;
  }
};

/**
 * Create a taxonomy annotation job (requires API key)
 * @param {string} fileId     - ID of the uploaded file
 * @param {string} tool       - Tool to use (blast or vsearch)
 * @param {string} algorithm  - Algorithm to use (BLAST tool: "blastn" or "megablast", VSEARCH tool: "usearch_global" or "search_exact")
 * @param {string} database   - Database to search against ("eukaryome_its", "eukaryome_ssu", "eukaryome_lsu", "eukaryome_long")
 * @param {Object} parameters - Additional parameters for the job
 * @returns {Promise<{job_id: string, status: string, message: string}>}
 */
export const createAnnotationJob = async (fileId, tool, algorithm, database, parameters = {}) => {

  try {
    const requestData = {
      file_id: fileId,
      tool,
      algorithm,
      database,
      parameters: {
        max_target_seqs: 20,
        num_threads: 4,
        ...parameters,
      },
    };

    return await secureApiCall('/api/v1/jobs', 'POST', requestData);
  } catch (error) {
    const isDevelopment = await con.isDevelopment()
    if (isDevelopment) {
      console.error('Job creation failed:', error);
    }
    throw error;
  }
};

/**
 * Check the status of a job (requires API key)
 * @param {string} jobId - ID of the job to check
 * @returns {Promise<{job_id: string, status: string, progress: number|null, started_at: string, completed_at: string|null}>}
 */
export const checkJobStatus = async (jobId) => {

  try {
    return await secureApiCall(`/api/v1/jobs/${jobId}/status`);
  } catch (error) {
    const isDevelopment = await con.isDevelopment()
    if (isDevelopment) {
      console.error('Job status check failed:', error);
    }
    throw error;
  }
};

/**
 * Get the results of a completed job (requires API key)
 * @param {string} jobId      - ID of the job to get results for
 * @returns {Promise<Object>} - The job results in JSON format
 */
export const getJobResults = async (jobId) => {
  try {
    return await secureApiCall(`/api/v1/jobs/${jobId}/results/json`);
  } catch (error) {
    const isDevelopment = await con.isDevelopment()
    if (isDevelopment) {
      console.error('Job results retrieval failed:', error);
    }
    throw error;
  }
};

/**
 * Get database versions and information (requires API key)
 * @returns {Promise<{refdbs: Array}>} - Database information including versions
 */
export const getDatabaseVersions = async () => {
  try {
    return await secureApiCall('/api/v1/refdbs');
  } catch (error) {
    const isDevelopment = await con.isDevelopment()
    if (isDevelopment) {
      console.error('Database versions retrieval failed:', error);
    }
    throw error;
  }
};
